(ns client-portal.client.audio.views
  (:require [clojure.string :as s]
            [shared.core.util :as util]
            [shared.core.components.components :as c]
            [cljs-time.core :as t]
            [cljs-time.coerce :as coerce]
            [shared.core.time :as time]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.components.theme :as theme]
            [shared.core.amelia-light.components.components :as alc]))

(defn audio-button [_]
  (let [hover?* (re/ratom false)]
    (fn [{:keys [icon-type style container-style disabled? size on-click]}]
      [c/interactive :span {:hover?* hover?*
                            :style (merge {:user-select :none} container-style)}
       [c/material-icon {:icon-type icon-type
                         :style (merge {:font-size size
                                        :color (style/color :primary :base)
                                        :cursor :pointer
                                        :transition "0.2s"
                                        :user-select :none}
                                       style
                                       (when disabled?
                                         {:cursor :default
                                          :opacity 0.5})
                                       (when (and @hover?* (not disabled?))
                                         {:transform "scale(1.1)"}))
                         :dark-style {:color (style/dark-color :primary :base)}
                         :on-click (when-not disabled? on-click)}]])))

(defn format-play-time [seconds]
  (-> (coerce/from-string "2022-05-11 00:00:00")
      (t/plus (t/seconds seconds))
      (time/as-format (if (> seconds (* 60 60)) "HH:mm:ss" "mm:ss"))))

(defn play-time [{:keys [seconds style]}]
  [theme/theme
   :span {:style (merge {:color (style/color :primary :base)
                         :font-size 14
                         :flex "0 0 60px"}
                        style)
          :theme (style/dark-style {:color (style/dark-color :primary :base)})}
   (format-play-time seconds)])

(defn playback-speed-icon [{:keys [color size]
                            :or {color "black"
                                 size 16}}]
  [:svg {:xmlns "http://www.w3.org/2000/svg" :width (str size) :height (str size) :viewBox (str "0 0 " size " " size) :fill "none"}
   [:path {:d "M9.44275 3.79138C12.2672 3.79138 14.5579 6.08126 14.558 8.90564C14.558 11.7301 12.2672 14.0209 9.44275 14.0209C7.31258 14.0208 5.49248 12.7226 4.72205 10.8763H1.36462V10.2289H4.50134C4.41055 9.88833 4.35379 9.53392 4.33533 9.16931L4.32849 8.90564H1.77185V8.25818H4.37048C4.42905 7.795 4.54813 7.35096 4.72205 6.93494H2.72888V6.28748H5.04919C5.942 4.79268 7.57536 3.79147 9.44275 3.79138ZM9.44275 4.9281C7.24359 4.92824 5.46533 6.70648 5.46521 8.90564C5.46521 11.1049 7.24352 12.884 9.44275 12.8842C11.6421 12.8842 13.4213 11.105 13.4213 8.90564C13.4211 6.70639 11.642 4.9281 9.44275 4.9281ZM9.72693 9.04822L12.0004 10.3949L11.5746 11.0941L8.87439 9.474V6.06384H9.72693V9.04822ZM15.5814 4.77283L14.7103 5.6449L12.9867 3.92419L13.8578 3.05212L15.5814 4.77283ZM11.6576 2.65564H6.97888V1.42322H11.6576V2.65564Z" :fill color}]])

(def playback-speed-options
  [{:text "1x" :value 1}
   {:text "1.25x" :value 1.25}
   {:text "1.5x" :value 1.5}
   {:text "2x" :value 2}
   {:text "3x" :value 3}])

(defn buffer-peaks [^js buffer {:keys [channel start end]} n]
  (let [data (.getChannelData buffer (or channel 0))
        ->idx #(-> % (* (.-sampleRate buffer)) (js/Math.round) (max 0) (min (.-length data)))
        from (->idx (or start 0))
        to (->idx (or end (.-duration buffer)))
        per-bar (/ (- to from) n)
        peaks (mapv (fn [i]
                      (let [slice-end (-> i (inc) (* per-bar) (+ from) (js/Math.ceil) (min to))]
                        (loop [j (-> i (* per-bar) (+ from) (js/Math.floor))
                               peak 0]
                          (if (< j slice-end)
                            (recur (inc j) (max peak (js/Math.abs (aget data j))))
                            peak))))
                    (range n))
        top (apply max peaks)]
    (cond->> peaks
             (pos? top) (mapv #(/ % top)))))

(defn waveform [_]
  (let [size* (re/ratom nil)
        size-ref (util/size-ref size*)
        hover-pct* (re/ratom nil)
        clip-id (str (gensym "audio-waveform-clip-"))
        bars* (atom nil)
        event->pct #(let [rect (-> % .-currentTarget .getBoundingClientRect)]
                      (-> (.-clientX %) (- (.-left rect)) (/ (.-width rect)) (max 0) (min 1)))]
    (fn [{:keys [buffer channel start end played-pct height on-seek-pct]
          :or {height 40
               played-pct 0}}]
      (let [n (some-> @size* :width (/ 3) (js/Math.floor) (max 1))
            cache-key [buffer channel start end n]
            path (if (= cache-key (:key @bars*))
                   (:path @bars*)
                   (let [path (when (and buffer n)
                                (->> (buffer-peaks buffer {:channel channel
                                                           :start start
                                                           :end end} n)
                                     (map-indexed (fn [i peak]
                                                    (let [h (max 4 (* peak 100))]
                                                      (str "M" (+ i 0.15) " " (/ (- 100 h) 2) "h0.7v" h "h-0.7z"))))
                                     (s/join)))]
                     (reset! bars* {:key cache-key
                                    :path path})
                     path))
            seekable? (and path on-seek-pct)
            hover-pct (when seekable? @hover-pct*)]
        [:div {:ref size-ref
               :style {:position :relative
                       :height height
                       :cursor (when seekable? :pointer)}
               :on-mouse-move (when seekable? #(reset! hover-pct* (event->pct %)))
               :on-mouse-leave #(reset! hover-pct* nil)
               :on-click (when seekable? #(on-seek-pct (event->pct %)))}
         (when path
           [:svg {:view-box (str "0 0 " n " 100")
                  :preserve-aspect-ratio "none"
                  :style {:display :block
                          :width "100%"
                          :height "100%"}}
            [:defs
             [:clipPath {:id (str clip-id "-played")}
              [:rect {:width (* played-pct n)
                      :height 100}]]
             [:clipPath {:id (str clip-id "-hover")}
              [:rect {:x (* played-pct n)
                      :width (-> hover-pct (or 0) (- played-pct) (max 0) (* n))
                      :height 100}]]]
            [:path {:d path
                    :style {:fill (style/alc-color "Text/secondary")}}]
            [:path {:d path
                    :clip-path (str "url(#" clip-id "-hover)")
                    :style {:fill (style/alc-color "Background/active_secondary")}}]
            [:path {:d path
                    :clip-path (str "url(#" clip-id "-played)")
                    :style {:fill (style/alc-color "Background/active")}}]])
         (when hover-pct
           [:div {:style {:position :absolute
                          :top 0
                          :bottom 0
                          :left (str (* hover-pct 100) "%")
                          :width 1
                          :background-color (style/alc-color "Common/active")
                          :pointer-events :none}}])]))))

(def slider-max 0.999999)

(defn player-bar [{:keys [playing? loading? disabled? position duration on-toggle on-seek on-play
                          playback-speed on-playback-speed-change]
                   waveform-props :waveform}]
  (let [position (min position duration)
        played-pct (if (pos? duration) (/ position duration) 0)
        done? (>= played-pct 1)
        speed? (some? on-playback-speed-change)
        controls-height 36
        bar-height 6
        waveform-gap 8]
    [:div {:style (cond-> {:display :grid
                           :grid-template-columns (if speed? "auto minmax(0, 1fr) auto auto" "auto minmax(0, 1fr) auto")
                           :align-items :center
                           :column-gap 10
                           :width "100%"}
                          waveform-props (assoc :grid-template-rows (str controls-height "px auto")))}
     (if loading?
       [:div {:style {:display :flex
                      :justify-content :center
                      :width 24}}
        [alc/gray-spinner {}]]
       [audio-button {:icon-type (if (and playing? (not done?)) :pause :play-arrow)
                      :size 24
                      :disabled? disabled?
                      :style {:color (style/alc-color "Icons/primary")}
                      :container-style {:display :flex}
                      :on-click on-toggle}])
     [:<>
      [:style "
              .simple-audio-player>input::-webkit-slider-thumb {
                visibility: hidden;
              }
              .simple-audio-player>input::-moz-range-thumb {
                visibility: hidden;
              }
              .simple-audio-player>input::-ms-thumb {
                visibility: hidden;
              }"]
      [c/primary-input-range {:value played-pct
                              :min 0
                              :max slider-max
                              :step :any
                              :disabled disabled?
                              :class-name "simple-audio-player"
                              :left-bar-style {:background-color (style/alc-color "Background/active")
                                               :border-radius (if done? 11 "11px 0px 0px 11px")}
                              :right-bar-style (if (and (zero? position) (not playing?))
                                                 {:background-color (style/alc-color "Text/secondary")
                                                  :border-radius 11}
                                                 {:background-color (style/alc-color "Text/secondary")
                                                  :border-radius "0px 11px 11px 0px"})
                              :on-change (when-not disabled?
                                           #(on-seek (-> % .-target .-value (js/parseFloat) (* duration))))}]]
     [:span {:style {:color (style/alc-color "Text/primary")
                     :font-size 13
                     :font-weight 400
                     :line-height 1.43
                     :width 80}}
      (str (format-play-time position)
           "/"
           (format-play-time duration))]
     (when speed?
       [:div {:style {:flex "0 0 auto"
                      :display :flex
                      :align-items :center
                      :gap 6}}
        [playback-speed-icon {:color (style/alc-color "Icons/secondary")
                              :size 16}]
        [alc/up-dropdown {:value playback-speed
                          :options playback-speed-options
                          :container-style {:flex "0 0 60px"}
                          :on-change on-playback-speed-change}]])
     (when waveform-props
       [:div {:style {:grid-column 2
                      :margin-top (- waveform-gap (/ (- controls-height bar-height) 2))}}
        [waveform (assoc waveform-props
                    :played-pct (min 1 (/ played-pct slider-max))
                    :on-seek-pct (when-not disabled?
                                   #(do
                                      (on-seek (* % duration))
                                      (when (and on-play (not playing?))
                                        (on-play)))))]])]))

(defn audio-player
  [{:keys [url extra-on-time-update on-pause on-play]}]
  (let [audio-el* (re/ratom nil)
        playable?* (re/ratom false)
        playing?* (re/ratom false)
        duration* (re/ratom 0)
        current-time* (re/ratom 0)
        play-audio-el (fn [play?]
                        (when (and @playable?* @audio-el*)
                          (if play?
                            (do
                              (.play @audio-el*)
                              (when on-play
                                (on-play @current-time*)))
                            (do
                              (.pause @audio-el*)
                              (when on-pause
                                (on-pause @current-time*))))))
        play-pause-audio-el #(let [playing? (and @audio-el* (not (.-paused @audio-el*)))]
                               (when-not (.hasAttribute @audio-el* "data-disabled")
                                 (play-audio-el (not playing?))))]
    (re/create-class
      {:component-did-mount
       (fn [this]
         (reset! audio-el* (some-> (re/dom-node this) (.querySelector "audio")))
         (when @audio-el*
           (.addEventListener @audio-el* "stalled" #(.load @audio-el*))
           (.addEventListener @audio-el* "canplay" #(reset! playable?* true))
           (.addEventListener @audio-el* "play" #(reset! playing?* true))
           (.addEventListener @audio-el* "pause" #(reset! playing?* false))
           (.addEventListener @audio-el* "durationchange" #(reset! duration* (.. % -target -duration)))
           (.addEventListener @audio-el* "timeupdate" #(when-let [cur-time (.. % -target -currentTime)]
                                                         (reset! current-time* cur-time)
                                                         (when extra-on-time-update
                                                           (extra-on-time-update @current-time*)))))
         (re/dispatch [:keybind/add-current-route-bindings
                       ["space"
                        ::toggle-play-pause
                        play-pause-audio-el]]))
       :component-will-unmount
       (fn [_]
         (re/dispatch [:keybind/remove-current-route-binding ::toggle-play-pause]))
       :reagent-render
       (fn [{:keys [audio-id url style on-seek disabled?] :as args}]
         (let [playing? @playing?*
               duration @duration*
               cur-time @current-time*
               active-played-pct (if (> duration 0) (/ cur-time duration) 0)
               done? (>= active-played-pct 1)
               seek-to #(when @audio-el*
                          (set! (.-currentTime @audio-el*) %)
                          (when on-seek (on-seek %)))]
           [theme/theme
            :div {:style (merge {:display :flex
                                 :flex-direction :column
                                 :align-items :center
                                 :justify-content :center
                                 :position :relative
                                 :height 80
                                 :padding "0 10px"
                                 :width "100%"}
                                style)
                  :theme (style/dark-style {:background-color (util/hex->rgba (style/dark-color :primary :base) 0.2)})}
            [:audio {:id audio-id
                     :data-disabled (when disabled? "true")
                     :src url
                     :preload :auto
                     :style {:height 0
                             :width 0}}]
            (when (and done? (not disabled?))
              [theme/theme
               :span {:style {:color (style/color :primary :base)
                              :font-size 14
                              :font-weight style/font-bold-weight
                              :text-transform :uppercase
                              :letter-spacing "0.8px"
                              :cursor :pointer
                              :position :absolute
                              :top 10
                              :right 10}
                      :theme (style/dark-style {:color (style/dark-color :primary :base)})
                      :on-click #(do
                                   (seek-to 0)
                                   (play-audio-el true))}
               "Restart"])
            [:div {:style {:display :flex
                           :align-items :center
                           :user-select :none}}
             [audio-button {:icon-type :replay-10
                            :size 30
                            :disabled? (or disabled? (= (or active-played-pct 0) 0))
                            :style {:margin-right 10}
                            :on-click (when-not disabled?
                                        #(let [cur-time (.-currentTime ^js @audio-el*)]
                                           (seek-to (max 0 (- cur-time 10)))))}]
             [audio-button {:icon-type (if (and playing? (not done?)) :pause-circle-outline :play-circle-outline)
                            :size 50
                            :disabled? disabled?
                            :on-click (when-not disabled?
                                        (if done?
                                          #(do
                                             (seek-to 0)
                                             (play-audio-el true))
                                          play-pause-audio-el))}]
             [audio-button {:icon-type :forward-10
                            :size 30
                            :disabled? (or disabled? done?)
                            :style {:margin-left 10}
                            :on-click #(let [duration (.-duration ^js @audio-el*)
                                             forward-10 (+ (.-currentTime ^js @audio-el*) 10)]
                                         (seek-to (if (> forward-10 duration) duration forward-10)))}]]
            [:div {:style {:display :flex
                           :align-items :center
                           :width "100%"}}
             [play-time {:style {:text-align :right
                                 :margin-right 5}
                         :seconds (* active-played-pct duration)}]
             [c/primary-input-range {:value active-played-pct
                                     :min 0
                                     :max 0.999999
                                     :disabled disabled?
                                     :step :any
                                     :style {:flex 1}
                                     :on-change (when-not disabled?
                                                  #(seek-to (* (js/parseFloat (.. % -target -value)) duration)))}]
             [play-time {:style {:margin-left 5}
                         :seconds duration}]]]))})))

(defn simple-audio-player
  [{:keys [extra-on-time-update on-pause on-play show-playback-speed? playback-speed-path space-keybinding?]
    :or {space-keybinding? true}}]
  (let [audio-el* (re/ratom nil)
        playable?* (re/ratom false)
        playing?* (re/ratom false)
        duration* (re/ratom 0)
        current-time* (re/ratom 0)
        playback-speed* (if playback-speed-path
                          (re/subscribe [:form/val- playback-speed-path :playback-speed])
                          (re/ratom 1))
        play-audio-el (fn [play?]
                        (when (and @playable?* @audio-el*)
                          (if play?
                            (do
                              (.play @audio-el*)
                              (when on-play
                                (on-play @current-time*)))
                            (do
                              (.pause @audio-el*)
                              (when on-pause
                                (on-pause @current-time*))))))
        play-pause-audio-el #(let [playing? (and @audio-el* (not (.-paused @audio-el*)))]
                               (when-not (.hasAttribute @audio-el* "data-disabled")
                                 (play-audio-el (not playing?))))]

    (re/create-class
      {:component-did-mount
       (fn [this]
         (reset! audio-el* (some-> (re/dom-node this) (.querySelector "audio")))
         (when @audio-el*
           (.addEventListener @audio-el* "stalled" #(.load @audio-el*))
           (.addEventListener @audio-el* "canplay" #(reset! playable?* true))
           (.addEventListener @audio-el* "play" #(reset! playing?* true))
           (.addEventListener @audio-el* "pause" #(reset! playing?* false))
           (.addEventListener @audio-el* "durationchange" #(reset! duration* (.. % -target -duration)))
           (.addEventListener @audio-el* "timeupdate" #(when-let [cur-time (.. % -target -currentTime)]
                                                         (reset! current-time* cur-time)
                                                         (when extra-on-time-update
                                                           (extra-on-time-update @current-time*))))
           (set! (.-playbackRate ^js @audio-el*) (or @playback-speed* 1)))
         (when space-keybinding?
           (re/dispatch [:keybind/add-current-route-bindings
                         ["space"
                          ::toggle-play-pause
                          play-pause-audio-el]])))
       :component-will-unmount
       (fn [_]
         (when space-keybinding?
           (re/dispatch [:keybind/remove-current-route-binding ::toggle-play-pause])))
       :reagent-render
       (fn [{:keys [audio-id url style on-seek disabled? waveform]}]
         (let [playing? @playing?*
               duration @duration*
               cur-time @current-time*
               done? (and (pos? duration) (>= cur-time duration))
               seek-to #(when @audio-el*
                          (set! (.-currentTime @audio-el*) %)
                          (when on-seek (on-seek %)))]
           [:div {:style (merge {:display :flex
                                 :padding "16px 13px"
                                 :width "100%"}
                                style)}
            [:audio {:id audio-id
                     :data-disabled (when disabled? "true")
                     :src url
                     :preload :auto
                     :style {:height 0
                             :width 0}}]
            [player-bar {:playing? playing?
                         :disabled? disabled?
                         :position cur-time
                         :duration duration
                         :on-toggle (if done?
                                      #(do
                                         (seek-to 0)
                                         (play-audio-el true))
                                      play-pause-audio-el)
                         :on-seek seek-to
                         :on-play #(play-audio-el true)
                         :playback-speed (or @playback-speed* 1)
                         :on-playback-speed-change (when show-playback-speed?
                                                     (fn [speed]
                                                       (if playback-speed-path
                                                         (re/dispatch [:form/change
                                                                       playback-speed-path
                                                                       :playback-speed
                                                                       speed])
                                                         (reset! playback-speed* speed))
                                                       (when @audio-el*
                                                         (set! (.-playbackRate ^js @audio-el*) speed))))
                         :waveform waveform}]]))})))